import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Copy, Link2, Loader2, KeyRound, UserRoundPlus, AlertTriangle } from "lucide-react";

const roleLabel = { admin: "Amministratore", member: "Membro", viewer: "Sola lettura" };

function ResetPasswordDialog({ member }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/team/${member.id}/reset-password`, { password });
      toast.success(`Password aggiornata per ${member.email}`);
      setOpen(false); setPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPassword(""); }}>
      <DialogTrigger asChild>
        <button className="text-muted-foreground hover:text-primary" title="Reimposta password" data-testid={`reset-pw-btn-${member.id}`}><KeyRound className="w-4 h-4" /></button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Reimposta password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <p className="text-sm text-muted-foreground">Membro: <span className="font-medium text-foreground">{member.email}</span></p>
          <div>
            <Label className="data-label">Nuova password (min. 8)</Label>
            <Input type="text" data-testid="reset-pw-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1 rounded-none font-mono" placeholder="Password iniziale" />
          </div>
          <p className="text-xs text-muted-foreground">Comunica la nuova password al membro; potrà cambiarla dopo l'accesso.</p>
          <DialogFooter>
            <Button type="submit" data-testid="reset-pw-submit-btn" disabled={loading} className="rounded-none w-full font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aggiorna password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ onCreated, disabled }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/team", form);
      toast.success(`Utente ${form.email} creato`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "member" });
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore creazione utente");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} className="rounded-none w-full" data-testid="create-user-btn">
          <UserRoundPlus className="w-4 h-4 mr-2" /> Crea utente
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Crea nuovo utente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="data-label">Nome</Label>
            <Input data-testid="new-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 rounded-none" placeholder="Mario Rossi" />
          </div>
          <div>
            <Label className="data-label">Email</Label>
            <Input type="email" data-testid="new-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 rounded-none" placeholder="mario@studio.it" />
          </div>
          <div>
            <Label className="data-label">Password (min. 8 caratteri)</Label>
            <Input type="text" data-testid="new-user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1 rounded-none font-mono" placeholder="Password iniziale" />
          </div>
          <div>
            <Label className="data-label">Ruolo</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Amministratore</SelectItem>
                <SelectItem value="member">Membro</SelectItem>
                <SelectItem value="viewer">Sola lettura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">Comunica le credenziali all'utente: potrà accedere subito.</p>
          <DialogFooter>
            <Button type="submit" data-testid="create-user-submit-btn" disabled={loading} className="rounded-none w-full font-medium">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea utente
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteRole, setInviteRole] = useState("member");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [m, i] = await Promise.all([api.get("/team"), api.get("/invites")]);
    setMembers(m.data); setInvites(i.data); setLoading(false);
    try { const s = await api.get("/subscription"); setSub(s.data); } catch { setSub(null); }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try { await api.put(`/team/${id}/role`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeUser = async (id) => {
    try { await api.delete(`/team/${id}`); toast.success("Membro rimosso"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createInvite = async () => {
    setCreating(true);
    try { await api.post("/invites", { role: inviteRole }); toast.success("Invito creato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const revokeInvite = async (id) => {
    await api.delete(`/invites/${id}`); toast.success("Invito revocato"); load();
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti");
  };

  const atUserLimit = !!(sub?.has_plan && sub.plan?.max_users != null && sub.usage?.users >= sub.plan.max_users);

  return (
    <Layout>
      <PageHeader title="Team" subtitle={`${members.length} membri`} />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border" data-testid="members-table">
          <div className="p-4 border-b border-border data-label">Membri del team</div>
          {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="data-label px-4 py-3">Nome</th>
                <th className="data-label px-4 py-3">Email</th>
                <th className="data-label px-4 py-3">Ruolo</th>
                <th className="data-label px-4 py-3 text-right">Azioni</th>
              </tr></thead>
              <tbody>
                {members.map((m, i) => (
                  <tr key={m.id} className={`border-b border-border ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`} data-testid={`member-row-${i}`}>
                    <td className="px-4 py-3 font-medium">{m.name} {m.id === user.id && <span className="data-label ml-1">(tu)</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.email}</td>
                    <td className="px-4 py-3">
                      {m.id === user.id ? (
                        <span className="font-mono text-xs">{roleLabel[m.role]}</span>
                      ) : (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="rounded-none h-8 w-40" data-testid={`role-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Amministratore</SelectItem>
                            <SelectItem value="member">Membro</SelectItem>
                            <SelectItem value="viewer">Sola lettura</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {m.id !== user.id && (
                        <div className="flex items-center justify-end gap-3">
                          <ResetPasswordDialog member={m} />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="text-muted-foreground hover:text-destructive" data-testid={`remove-member-${i}`}><Trash2 className="w-4 h-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-none">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-heading font-black tracking-tight">Rimuovere {m.email}?</AlertDialogTitle>
                                <AlertDialogDescription>L'utente non potrà più accedere allo studio. Azione irreversibile.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-remove-member-${i}`} onClick={() => removeUser(m.id)} className="rounded-none bg-destructive hover:bg-destructive/90">Rimuovi</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-border p-5" data-testid="create-user-card">
            <div className="flex items-center gap-2 mb-3"><UserRoundPlus className="w-4 h-4 text-primary" /><h3 className="font-heading font-semibold tracking-tight">Crea utente</h3></div>
            {sub?.has_plan && (
              <div className="text-sm text-muted-foreground mb-3 flex items-center justify-between" data-testid="team-usage-line">
                <span>Utenti del piano {sub.plan.name}</span>
                <span className={`font-mono ${atUserLimit ? "text-red-600 font-semibold" : ""}`}>{sub.usage.users} / {sub.plan.max_users == null ? "∞" : sub.plan.max_users}</span>
              </div>
            )}
            {atUserLimit && (
              <p className="text-xs text-red-600 mb-3 flex items-start gap-1" data-testid="user-limit-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Limite utenti raggiunto. Aggiorna il piano per aggiungere altri utenti.
              </p>
            )}
            <CreateUserDialog onCreated={load} disabled={atUserLimit} />
            <p className="text-xs text-muted-foreground mt-2">Crei l'account con email e password da comunicare direttamente.</p>
          </div>

          <div className="bg-white border border-border p-5">
            <div className="flex items-center gap-2 mb-4"><UserPlus className="w-4 h-4 text-primary" /><h3 className="font-heading font-semibold tracking-tight">Invita membro</h3></div>
            <Label className="data-label">Ruolo</Label>
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger className="rounded-none mt-1" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Amministratore</SelectItem>
                <SelectItem value="member">Membro</SelectItem>
                <SelectItem value="viewer">Sola lettura</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={createInvite} disabled={creating || atUserLimit} className="rounded-none w-full mt-3" data-testid="create-invite-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Genera link invito
            </Button>
            {atUserLimit && <p className="text-xs text-red-600 mt-2">Limite utenti del piano raggiunto: non è possibile generare nuovi inviti.</p>}
          </div>

          <div className="bg-white border border-border p-5" data-testid="invites-list">
            <div className="data-label mb-3">Inviti attivi ({invites.length})</div>
            <div className="space-y-3">
              {invites.length === 0 && <p className="text-sm text-muted-foreground">Nessun invito attivo.</p>}
              {invites.map((inv) => (
                <div key={inv.id} className="border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono bg-secondary px-2 py-0.5">{roleLabel[inv.role]}</span>
                    <button onClick={() => revokeInvite(inv.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => copyLink(inv.token)} className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`copy-invite-${inv.id}`}>
                    <Copy className="w-3 h-3" /> Copia link
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
